import { BlogArticle, BlogArticleKey } from "@/app/types/BlogArticle";
import { ArticleBlockType } from "@/app/types/BlogArticleContent";
import { BlogCategory } from "@/app/types/BlogCategory";
import { BlogSlugs, PublicPaths } from "@/app/vars/Routes";

export const discoveryCallArticle: BlogArticle = {
  key: BlogArticleKey.DISCOVERY_CALL,
  slug: BlogSlugs.DISCOVERY_CALL,
  categoryId: BlogCategory.CALL_ANALYSIS,
  date: "2026-08-30",
  updated: "2026-09-29",
  readTime: 8,
  locales: {
    fr: {
      title: "Appel de découverte : les questions qui changent la suite",
      excerpt:
        "BANT qualifiait pour éliminer. Ce qui fonctionne aujourd'hui cherche l'inverse : la situation, la conséquence chiffrée, et l'échéance qui force la décision.",
      description:
        "Mener un appel de découverte qui sert : le cadre qui a remplacé BANT, le bon nombre de questions, la part de parole, et quoi relire après.",
      ctaTitle: "Relisez vos découvertes au lieu de les oublier",
      ctaText:
        "Importez un appel de découverte dans la fiche du client : on le transcrit, on étiquette la douleur, les objections, le budget et l'échéance, et on relève les engagements pris. Sans engagement.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Un appel de découverte raté ne se voit pas sur le moment. Il se voit trois semaines plus tard, quand la relance reste sans réponse et que personne ne sait dire pourquoi.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Dans la plupart des cas, la cause est la même : l'appel s'est bien passé. Le courant est passé, on a parlé de tout, on a même montré le produit. Et à la fin, personne ne peut répondre à la seule question qui compte : qu'est-ce qui va faire que cette personne achète maintenant plutôt que dans un an.",
        },
        { type: ArticleBlockType.H2, text: "Pourquoi BANT ne suffit plus" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "BANT demandait quatre choses : budget, autorité, besoin, échéance. Le cadre a été construit pour trier vite dans un monde où le budget existait avant la conversation.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Ce n'est plus la façon dont on achète. Dans la plupart des ventes B2B d'aujourd'hui, le budget se débloque après que la douleur a été reconnue, pas avant. Qualifier sur le budget en début d'appel revient donc à éliminer des affaires qui auraient signé, simplement parce que la ligne budgétaire n'existait pas encore.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "BANT cherche une raison de sortir. Un bon appel de découverte cherche ce qui rendrait le statu quo plus coûteux que le changement.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Les cadres qui l'ont remplacé, SPICED en tête, gardent la même exigence mais changent l'ordre : on part de la situation, on va chercher la douleur, on la chiffre, on identifie l'échéance qui force la décision, et on ne parle du processus de décision qu'ensuite.",
        },
        { type: ArticleBlockType.H2, text: "Les cinq choses à ressortir d'un appel" },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**La situation.** Comment ça se passe aujourd'hui, concrètement, avec des chiffres et un déroulé, pas une description de poste.",
            "**La douleur.** Ce qui coince dans cette situation, dit avec les mots de la personne, pas reformulé dans les vôtres.",
            "**La conséquence.** Ce que ça coûte réellement. Sans ce point, il n'y a pas d'arbitrage possible face à toutes les autres priorités.",
            "**L'échéance.** Ce qui fait que ça doit être réglé maintenant. Un lancement, un départ, un renouvellement, une saison.",
            "**La décision.** Qui d'autre est concerné, et selon quel chemin ça se valide.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "L'ordre compte autant que la liste. La conséquence chiffrée doit venir avant la question du budget, parce que c'est elle qui rend le budget pensable. Poser l'inverse, c'est demander à quelqu'un de payer pour un problème qu'il n'a pas encore mesuré.",
        },
        { type: ArticleBlockType.H2, text: "Trois réglages qui pèsent plus que le script" },
        { type: ArticleBlockType.H3, text: "1. Moins de questions, mieux placées" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Les analyses publiées sur de gros volumes d'appels convergent : les affaires gagnées ne sont pas celles où l'on a posé le plus de questions. Passé une quinzaine, l'appel bascule dans l'interrogatoire et la personne se ferme. Ce qui distingue les bons appels n'est pas le nombre, c'est le fait de creuser les deux ou trois réponses qui comptent au lieu de dérouler une liste.",
        },
        { type: ArticleBlockType.H3, text: "2. Parler moins de la moitié du temps" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "C'est le réglage le plus simple à mesurer et le plus difficile à tenir. Sur un appel de découverte, la part de parole du vendeur devrait rester sous la moitié, et plutôt nettement. Chaque minute de démonstration non demandée est une minute où l'on n'apprend rien.",
        },
        { type: ArticleBlockType.H3, text: "3. Dix minutes de préparation" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Toute question dont la réponse est sur le site de la personne est une question gaspillée. Dix minutes avant l'appel suffisent à supprimer les questions génériques et à en poser une seule qui montre que vous avez regardé. C'est ce qui fait basculer la conversation d'un questionnaire vers un échange.",
        },
        { type: ArticleBlockType.H2, text: "Ce qui change entre les deux appels" },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "L'appel qui s'est bien passé",
            items: [
              "On a parlé de tout, sans creuser",
              "Le produit a été montré avant qu'on le demande",
              "Le besoin est décrit avec vos mots",
              "Aucun chiffre sur ce que le problème coûte",
              "Prochaine étape : « je reviens vers vous »",
            ],
          },
          good: {
            title: "L'appel qui sert",
            items: [
              "Deux ou trois sujets creusés jusqu'au fond",
              "Le produit évoqué en réponse à une question posée",
              "Le besoin cité avec les mots de la personne",
              "Une conséquence chiffrée, même approximative",
              "Prochaine étape : une date et une raison",
            ],
          },
        },
        { type: ArticleBlockType.H2, text: "La partie que presque personne ne fait" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Tout ce qui précède se joue pendant l'appel. Or l'essentiel de la valeur arrive après, et c'est là que ça se perd : personne ne réécoute.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Ce qu'un appel contient dépasse largement le deal en cours. Les mots que la personne emploie pour décrire son problème sont ceux qu'il faut reprendre dans une page ou un message. Les objections qui reviennent d'un appel à l'autre disent ce que votre offre n'explique pas encore. La situation de départ, répétée par dix interlocuteurs, dessine votre client type mieux que n'importe quel atelier.",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["Ce que vous relisez", "Ce que ça alimente"],
          rows: [
            ["Les mots employés pour dire le problème", "Votre discours, vos pages, vos relances"],
            ["Les objections qui reviennent", "Ce que votre argumentaire doit lever plus tôt"],
            ["La situation de départ répétée", "Le portrait de votre client type"],
            ["Ce qui a déclenché la recherche", "Le signal à guetter chez les prochains"],
          ],
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Là où Meidly aide",
          text: "Chaque appel de découverte rejoint la fiche du client. On le transcrit, on étiquette les passages qui comptent, douleur, raison d'achat, objection, processus de décision, budget, échéance, et on relève les engagements pris avec leur date. D'un client à l'autre, les clients qui achètent pour les mêmes raisons sont regroupés en personas, avec leurs propres mots, au lieu de rester dans un enregistrement que personne ne rouvre.",
          linkLabel: "Voir les fiches clients",
          linkPath: PublicPaths.HOME,
        },
        { type: ArticleBlockType.H2, text: "En résumé" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Cherchez la situation avant le budget, chiffrez la conséquence avant de proposer, et gardez l'échéance en tête tout du long. Posez moins de questions et creusez davantage. Puis relisez, parce qu'un appel de découverte ne sert pas qu'au deal qu'il concerne : il vous apprend à qui vous vendez, et avec quels mots.",
        },
      ],
      faq: [
        {
          question: "Quelles questions poser lors d'un appel de découverte ?",
          answer:
            "Des questions qui font raconter la situation actuelle avec des chiffres et un déroulé, puis ce qui coince dedans, puis ce que ça coûte. Ensuite seulement l'échéance et le circuit de décision. Une bonne question de découverte appelle un récit, pas un oui ou un non.",
        },
        {
          question: "BANT est-il encore utile ?",
          answer:
            "Comme aide-mémoire, oui. Comme grille de qualification en début d'appel, il élimine des affaires qui auraient signé : dans beaucoup de ventes B2B, le budget se débloque après que la douleur a été reconnue. Chiffrez la conséquence d'abord, parlez budget ensuite.",
        },
        {
          question: "Combien de questions poser sur un appel de découverte ?",
          answer:
            "Une quinzaine suffit largement. Au-delà, l'échange devient un interrogatoire et la personne se ferme. Ce qui fait la différence n'est pas le nombre mais la profondeur : deux ou trois sujets creusés valent mieux qu'une liste déroulée.",
        },
        {
          question: "Quelle part de parole viser en découverte ?",
          answer:
            "Moins de la moitié, et plutôt nettement. Si vous parlez plus que la personne en face, vous êtes en démonstration, pas en découverte. C'est l'indicateur le plus simple à mesurer sur un enregistrement, et le plus révélateur.",
        },
        {
          question: "Que faire de ses appels une fois passés ?",
          answer:
            "Les relire pour ce qui dépasse le deal : les mots employés pour décrire le problème, les objections qui reviennent, la situation de départ commune à plusieurs interlocuteurs. C'est cette matière qui nourrit votre discours, votre ciblage et la préparation des appels suivants.",
        },
      ],
    },
    en: {
      title: "Discovery calls: the questions that change what happens next",
      excerpt:
        "BANT qualified in order to rule out. What works now looks for the opposite: the situation, the quantified consequence, and the deadline that forces a decision.",
      description:
        "Running a discovery call that serves you: the framework that replaced BANT, how many questions, how much talking, and what to read after.",
      ctaTitle: "Read your discovery calls back instead of forgetting them",
      ctaText:
        "Import a discovery call into the customer file: we transcribe it, tag the pain, the objections, the budget and the timeline, and pick out the commitments made. No strings attached.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "A failed discovery call does not look like one at the time. It shows three weeks later, when the follow-up goes unanswered and nobody can say why.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "In most cases the cause is the same: the call went well. The rapport was there, everything got covered, the product even got shown. And by the end, nobody can answer the only question that matters: what will make this person buy now rather than in a year.",
        },
        { type: ArticleBlockType.H2, text: "Why BANT is no longer enough" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "BANT asked for four things: budget, authority, need, timing. The framework was built to sort fast in a world where the budget existed before the conversation.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "That is no longer how buying works. In most B2B sales today, budget is freed after the pain has been acknowledged, not before. Qualifying on budget early therefore rules out deals that would have closed, purely because the budget line did not exist yet.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "BANT looks for a reason to walk away. A good discovery call looks for what would make standing still cost more than changing.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The frameworks that replaced it, SPICED chief among them, keep the same rigour but change the order: start from the situation, dig into the pain, quantify it, find the deadline that forces the decision, and only then talk about how it gets approved.",
        },
        { type: ArticleBlockType.H2, text: "The five things to come away with" },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**The situation.** How things work today, concretely, with numbers and a step by step account, not a job description.",
            "**The pain.** What breaks in that situation, in the person's own words, not reworded into yours.",
            "**The consequence.** What it actually costs. Without this, there is no way to weigh it against every other priority.",
            "**The deadline.** What makes it need fixing now. A launch, a departure, a renewal, a season.",
            "**The decision.** Who else is involved, and along which path it gets approved.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The order matters as much as the list. The quantified consequence has to come before the budget question, because it is what makes a budget thinkable. Asking the other way round is asking someone to pay for a problem they have not measured yet.",
        },
        { type: ArticleBlockType.H2, text: "Three settings that weigh more than the script" },
        { type: ArticleBlockType.H3, text: "1. Fewer questions, better placed" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Published analyses across large call volumes converge: won deals are not the ones with the most questions. Past fifteen or so, the call tips into an interrogation and the person closes up. What sets good calls apart is not the count, it is digging into the two or three answers that matter instead of working through a list.",
        },
        { type: ArticleBlockType.H3, text: "2. Talk less than half the time" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "This is the easiest setting to measure and the hardest to hold. On a discovery call the seller's share of talk should stay under half, and comfortably so. Every minute of unrequested demo is a minute where you learn nothing.",
        },
        { type: ArticleBlockType.H3, text: "3. Ten minutes of preparation" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Any question whose answer is on the person's website is a wasted question. Ten minutes before the call is enough to cut the generic ones and ask a single one that shows you looked. That is what tips the conversation from a questionnaire into an exchange.",
        },
        { type: ArticleBlockType.H2, text: "What separates the two calls" },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "The call that went well",
            items: [
              "Everything got covered, nothing got dug into",
              "The product was shown before anyone asked",
              "The need is described in your words",
              "No number on what the problem costs",
              "Next step: I will get back to you",
            ],
          },
          good: {
            title: "The call that serves you",
            items: [
              "Two or three subjects taken all the way down",
              "The product mentioned in answer to a question",
              "The need quoted in the person's own words",
              "A quantified consequence, even a rough one",
              "Next step: a date and a reason",
            ],
          },
        },
        { type: ArticleBlockType.H2, text: "The part almost nobody does" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Everything above happens during the call. Yet most of the value arrives afterwards, and that is where it is lost: nobody listens back.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "What a call holds goes well beyond the deal at hand. The words a person uses to describe their problem are the ones to reuse on a page or in a message. The objections that recur from one call to the next say what your offer does not yet explain. The starting situation, repeated by ten people, draws your typical customer better than any workshop.",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["What you read back", "What it feeds"],
          rows: [
            ["The words used for the problem", "Your pitch, your pages, your follow ups"],
            ["The objections that recur", "What your pitch has to answer earlier"],
            ["The repeated starting situation", "The portrait of your typical customer"],
            ["What triggered the search", "The signal to watch for in the next ones"],
          ],
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Where Meidly helps",
          text: "Every discovery call joins the customer file. We transcribe it, tag the passages that matter, pain, buying reason, objection, decision process, budget, timeline, and pick out the commitments made with their due date. Across customers, those who buy for the same reasons are grouped into personas, in their own words, instead of sitting in a recording nobody reopens.",
          linkLabel: "See the customer files",
          linkPath: PublicPaths.HOME,
        },
        { type: ArticleBlockType.H2, text: "In short" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Look for the situation before the budget, quantify the consequence before proposing anything, and keep the deadline in view throughout. Ask fewer questions and dig deeper. Then read it back, because a discovery call does not only serve the deal it belongs to: it teaches you who you sell to, and in which words.",
        },
      ],
      faq: [
        {
          question: "What questions should you ask on a discovery call?",
          answer:
            "Questions that make someone describe their current situation with numbers and a step by step account, then what breaks in it, then what that costs. Only then the deadline and the approval path. A good discovery question invites a story, not a yes or a no.",
        },
        {
          question: "Is BANT still useful?",
          answer:
            "As a memory aid, yes. As an early qualification grid it rules out deals that would have closed: in many B2B sales, budget is freed after the pain is acknowledged. Quantify the consequence first, talk budget second.",
        },
        {
          question: "How many questions should a discovery call have?",
          answer:
            "Around fifteen is plenty. Beyond that the exchange becomes an interrogation and the person closes up. The difference is not the count but the depth: two or three subjects taken all the way down beat a list worked through.",
        },
        {
          question: "How much should you talk on a discovery call?",
          answer:
            "Less than half the time, and comfortably so. If you are talking more than the person opposite, you are demoing, not discovering. It is the simplest thing to measure on a recording, and the most revealing.",
        },
        {
          question: "What should you do with calls once they are over?",
          answer:
            "Read them back for what outlives the deal: the words used to describe the problem, the objections that recur, the starting situation shared by several people. That material is what feeds your pitch, your targeting and how you prepare the next calls.",
        },
      ],
    },
  },
};
